import { useQuery } from '@tanstack/react-query'
import { hasLiveApi } from '@/api/client'
import { keys } from '@/api/hooks/shared/keys'
import { searchHierarchyNew, searchHierarchyNewUsernames } from '@/api/map/member'

export type SearchHierarchyNewOptions = {
  /** Lab ui-select refresh on open — empty filter → API default prefix `a`. */
  prefetchOnEmpty?: boolean
}

/**
 * Downline username autocomplete for Lab-style member pickers, including
 * PL Statement and workstation bet breakdown.
 *
 * Debouncing belongs in the caller (Lab ui-select used 100ms). This hook is a
 * plain function of the filter text it receives.
 *
 * Uses hierarchy only (`hasLiveApi`) — not workstation `live`, which also
 * requires the bet-service URL.
 */
export function useSearchHierarchyNew(
  filter: string,
  enabled: boolean,
  opts?: SearchHierarchyNewOptions,
) {
  const trimmed = filter.trim()
  const query = { filter: trimmed, page: 1 as const }
  const shouldFetch = opts?.prefetchOnEmpty ? true : trimmed.length >= 1

  return useQuery({
    queryKey: keys.searchHierarchyNew(query),
    queryFn: async () => {
      const rows = await searchHierarchyNew({ filter: trimmed, page: 1 })
      return searchHierarchyNewUsernames(rows)
    },
    enabled: enabled && hasLiveApi() && shouldFetch,
    placeholderData: (previous) => previous,
    staleTime: 10_000,
    gcTime: 60_000,
    retry: false,
  })
}
